/**
 * ScoreboardCard — Apr 30 2026
 *
 * Big-finger-friendly live scoreboard for game pages. Two huge
 * numeric displays for home and away with +/- buttons below each.
 * Designed for touch-first use during games, on a phone propped
 * up on the bench or held by a kid keeping score.
 *
 * Permissions:
 *   • Always readable by any team member.
 *   • Edit gated by team setting `scoreboard_coach_only`. When
 *     true → only coaches/managers can edit. When false (default)
 *     → every roster member can edit.
 *   • The backend GET /scoreboard returns canEdit + coachOnlyMode
 *     so this component doesn't have to know the user's role —
 *     it just renders disabled buttons + a "coach-only" hint when
 *     canEdit is false.
 *
 * Real-time:
 *   • Subscribes to chatSocket SCOREBOARD_UPDATE events for THIS
 *     game's id and updates state when other clients edit. No
 *     polling — the server fans out via WebSocket and we trust
 *     the existing reconnect logic in socket.js.
 *   • If WebSocket is disconnected (reconnecting after a drop),
 *     we re-fetch on next visibility change so any missed events
 *     during the gap don't leave us with stale numbers.
 *
 * Optimistic UI:
 *   • The +/- buttons set state locally first, then PATCH the
 *     server. If the server rejects (e.g. coach flipped the
 *     toggle mid-game), we roll back. Avoids a 200-300ms feel
 *     of "tap, then wait for the network".
 *
 * Reset:
 *   • Coach/manager only. Confirms before clearing. Hidden in
 *     UI for non-coaches since they couldn't use it anyway.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Minus, RotateCcw, Lock, Save } from 'lucide-react';
import api from '../../services/api.js';
import { chatSocket } from '../../services/socket.js';

export default function ScoreboardCard({ teamId, gameId, isCoach = false, isManager = false, onSavedAndNew }) {
  const [score, setScore] = useState({ home: 0, away: 0 });
  const [meta, setMeta]   = useState({ updatedBy: null, updatedByName: null, updatedAt: null });
  const [canEdit, setCanEdit] = useState(false);
  const [coachOnlyMode, setCoachOnlyMode] = useState(false);
  // Apr 30 2026 (followup) — master on/off, sourced from
  // team.settings.scoreboard_enabled. When false, this component
  // renders nothing so the card vanishes from game pages and the
  // dashboard hero. Default true for existing teams.
  const [scoreboardEnabled, setScoreboardEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Generation marker for stale-event guarding. Bumped on every
  // server-confirmed response so late-arriving WS events from
  // earlier writes don't clobber a newer state.
  const lastUpdatedAtRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/games/${gameId}/scoreboard`);
      setScore({ home: data.home_score, away: data.away_score });
      setMeta({
        updatedBy: data.updated_by,
        updatedByName: data.updated_by_name,
        updatedAt: data.updated_at,
      });
      setCanEdit(!!data.canEdit);
      setCoachOnlyMode(!!data.coachOnlyMode);
      // Coerce undefined → true so older backends without this
      // field don't accidentally hide the scoreboard everywhere.
      setScoreboardEnabled(data.scoreboardEnabled !== false);
      lastUpdatedAtRef.current = data.updated_at ? new Date(data.updated_at).getTime() : 0;
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load scoreboard');
    } finally {
      setLoading(false);
    }
  }, [teamId, gameId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Subscribe to live updates. The handler receives msg.data (see
  // socket.js dispatch — it splits {type, data}). Filter to OUR
  // game (the team's chat socket carries every game's scoreboard
  // update for that team).
  //
  // Note: chatSocket.on() returns an unsubscribe FUNCTION (not an
  // off() pair). Calling that function removes just this listener.
  useEffect(() => {
    const handler = (data) => {
      if (!data || Number(data.gameId) !== Number(gameId)) return;
      // Stale-event guard: ignore events older than the latest
      // server response we processed. Prevents flicker when an
      // optimistic local update beats the broadcast back.
      const evtTs = data.updated_at ? new Date(data.updated_at).getTime() : 0;
      if (evtTs && evtTs < lastUpdatedAtRef.current) return;

      setScore({ home: data.home_score, away: data.away_score });
      setMeta({
        updatedBy: data.updated_by,
        updatedByName: null, // event doesn't carry name; refresh on tap or next mount
        updatedAt: data.updated_at,
      });
      lastUpdatedAtRef.current = evtTs || lastUpdatedAtRef.current;
    };
    const unsubscribe = chatSocket.on('SCOREBOARD_UPDATE', handler);
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [gameId]);

  // Re-sync on visibility change — if the user backgrounded the
  // tab or the socket dropped silently, we might be behind.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refresh]);

  // Send a delta update with optimistic UI.
  const adjust = useCallback(async (side, delta) => {
    if (!canEdit || busy) return;
    // Capture pre-state for rollback before mutating.
    const before = { ...score };
    const next = { ...score };
    next[side] = Math.max(0, Math.min(99, score[side] + delta));
    if (next[side] === score[side]) return; // already at boundary

    setScore(next);
    setBusy(true);
    try {
      const body = side === 'home' ? { delta_home: delta } : { delta_away: delta };
      const { data } = await api.patch(`/teams/${teamId}/games/${gameId}/scoreboard`, body);
      // Trust the server's clamped result.
      setScore({ home: data.home_score, away: data.away_score });
      setMeta((m) => ({ ...m, updatedAt: data.updated_at, updatedBy: data.updated_by }));
      lastUpdatedAtRef.current = data.updated_at ? new Date(data.updated_at).getTime() : Date.now();
      setError(null);
    } catch (e) {
      // Roll back optimistic state.
      setScore(before);
      setError(e.response?.data?.error || 'Failed to update');
      // If the server returned 403, the toggle probably flipped —
      // refresh perms so we lock the UI.
      if (e.response?.status === 403) refresh();
    } finally {
      setBusy(false);
    }
  }, [canEdit, busy, score, teamId, gameId, refresh]);

  // Coach reset — confirm, then call the dedicated reset endpoint.
  const reset = useCallback(async () => {
    if (!(isCoach || isManager) || busy) return;
    if (!window.confirm('Reset the scoreboard to 0-0?')) return;
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/scoreboard/reset`);
      setScore({ home: 0, away: 0 });
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to reset');
    } finally {
      setBusy(false);
    }
  }, [isCoach, isManager, busy, teamId, gameId]);

  // May 2 2026 — coach feature: "Save this mini-game and start a new
  // one." Used during sessions where the team plays 3-4 mini-games on
  // the same date. Server-side this:
  //   • Marks the current game as completed (final score baked in)
  //   • Creates a sibling game with parent_game_id pointing at the
  //     session root, same arena/season/date, time bumped by N seconds
  //   • Copies the same roster (game_availability rows, minus 'out')
  //   • Returns the new game id
  // The host page (GameScoreboardPage) passes a callback to navigate
  // the user to the new mini-game's scoreboard. If no callback is
  // provided we just refresh in place — defensive for the legacy embed.
  // 2026-07-10 (batch-B E3, per Gilles) — plain SAVE: lock the current
  // score as this mini's final result WITHOUT creating a new sibling.
  // Before this, the only finalize path was Save & New, which always
  // forced a phantom extra mini-game at the end of the night.
  const saveOnly = useCallback(async () => {
    if (!(isCoach || isManager) || busy) return;
    const msg =
      `Save this game (Home ${score.home} — Away ${score.away})?\n\n` +
      `The score is locked in as the final result. No new mini-game ` +
      `is created.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/games/${gameId}/scoreboard/save`);
      setError(null);
      await refresh(); // card re-reads as completed/locked
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save the game');
    } finally {
      setBusy(false);
    }
  }, [isCoach, isManager, busy, score.home, score.away, teamId, gameId, refresh]);

  const saveAndNew = useCallback(async () => {
    if (!(isCoach || isManager) || busy) return;
    const msg =
      `Save this mini-game (Home ${score.home} — Away ${score.away}) ` +
      `and start a fresh mini-game at 0-0?\n\n` +
      `The current score will be locked in as the final result for this ` +
      `game, a new mini-game will be created on the same date with the ` +
      `same roster, and you'll be taken to its scoreboard.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/teams/${teamId}/games/${gameId}/scoreboard/save-and-new`
      );
      setError(null);
      if (typeof onSavedAndNew === 'function' && data?.newGameId) {
        onSavedAndNew(data.newGameId, data);
      } else {
        // Legacy fallback: no host callback. Refresh shows the
        // current game now reads as locked/completed.
        await refresh();
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save and start new mini-game');
    } finally {
      setBusy(false);
    }
  }, [isCoach, isManager, busy, score.home, score.away, teamId, gameId, onSavedAndNew, refresh]);

  if (loading) {
    return (
      <div style={cardWrap}>
        <div style={{ ...header, opacity: 0.5 }}>SCOREBOARD</div>
        <div style={{ textAlign: 'center', padding: 28, color: 'var(--text-3)' }}>Loading…</div>
      </div>
    );
  }

  // Apr 30 2026 (followup) — feature is disabled team-wide. Render
  // nothing so the host pages (game detail / dashboard hero) don't
  // show a scoreboard slot at all. The coach can re-enable in Team
  // Settings → Scoreboard.
  if (!scoreboardEnabled) return null;

  return (
    <div style={cardWrap}>
      <div style={header}>
        SCOREBOARD
        {coachOnlyMode && (
          <span style={lockHint} title="Coaches only — flip in Team Settings → Scoreboard">
            <Lock size={11} /> COACH ONLY
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 8px 8px' }}>
        {/* HOME side */}
        <ScoreSide
          label="HOME"
          value={score.home}
          canEdit={canEdit}
          busy={busy}
          onInc={() => adjust('home', +1)}
          onDec={() => adjust('home', -1)}
        />
        {/* AWAY side */}
        <ScoreSide
          label="AWAY"
          value={score.away}
          canEdit={canEdit}
          busy={busy}
          onInc={() => adjust('away', +1)}
          onDec={() => adjust('away', -1)}
        />
      </div>

      {/* Footer — last update + coach actions (Save & New + Reset).
          May 2 2026: added Save & Start New for mini-game sessions
          where the team plays 3-4 games on one date. Coach-only.
          Reset stays as the destructive "wipe to 0-0" action; Save
          & New is the constructive "lock the score, start fresh"
          path. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, padding: '8px 14px 12px',
        fontSize: 11, color: 'var(--text-3)',
        borderTop: '1px solid var(--border)',
        marginTop: 4,
        flexWrap: 'wrap',
      }}>
        <span style={{ flex: '1 1 auto', minWidth: 0 }}>
          {meta.updatedAt
            ? `Updated ${new Date(meta.updatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}${meta.updatedByName ? ` by ${meta.updatedByName}` : ''}`
            : 'No score yet'}
        </span>
        {(isCoach || isManager) && (
          // 2026-07-14 (mobile pass) — third button (plain "Save") added
          // 2026-07-10 alongside the existing Save & New + Reset, and this
          // row never got flex-wrap: at ~390px, with the timestamp text
          // also fighting for space on the same line, three icon+label
          // buttons could push past the card edge. flexWrap lets the
          // buttons drop to a second line instead of clipping/overflowing;
          // justifyContent keeps them right-aligned like the single-line
          // case already looked.
          <div style={{ display: 'flex', gap: 6, flex: '0 0 auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* 2026-07-10 (batch-B E3) — plain Save (no new sibling). */}
            <button onClick={saveOnly} disabled={busy} style={saveBtn}
              title="Save this game's final score — no new mini-game is created">
              <Save size={12} /> Save
            </button>
            <button onClick={saveAndNew} disabled={busy} style={saveBtn}
              title="Save this mini-game and start a new one at 0-0">
              <Save size={12} /> Save &amp; New
            </button>
            <button onClick={reset} disabled={busy} style={resetBtn} title="Reset to 0-0">
              <RotateCcw size={12} /> Reset
            </button>
          </div>
        )}
      </div>

      {!canEdit && coachOnlyMode && (
        <div style={hintBanner}>
          The coach has set this scoreboard to coach-only. Ask a coach
          to update the score, or flip the toggle in Team Settings → Scoreboard.
        </div>
      )}

      {error && (
        <div style={{ ...hintBanner, color: '#ff6b6b', borderColor: 'rgba(255,80,80,0.3)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

// One side of the scoreboard (HOME or AWAY).
function ScoreSide({ label, value, canEdit, busy, onInc, onDec }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '14px 6px 12px',
      background: 'rgba(0,212,255,0.04)',
      border: '1px solid rgba(0,212,255,0.10)',
      borderRadius: 12,
    }}>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
        color: 'var(--text-2)', marginBottom: 6,
      }}>{label}</div>

      {/* Big number */}
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: 56, fontWeight: 900, lineHeight: 1,
        color: 'var(--ice)',
        textShadow: '0 0 24px rgba(0,212,255,0.35)',
        fontVariantNumeric: 'tabular-nums',
        userSelect: 'none',
      }}>{value}</div>

      {/* +/- buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={onDec}
          disabled={!canEdit || busy || value <= 0}
          style={btn(canEdit && value > 0)}
          aria-label={`Decrement ${label} score`}
        ><Minus size={20} /></button>
        <button
          onClick={onInc}
          disabled={!canEdit || busy || value >= 99}
          style={btn(canEdit && value < 99)}
          aria-label={`Increment ${label} score`}
        ><Plus size={20} /></button>
      </div>
    </div>
  );
}

// ─── styles ────────────────────────────────────────────────────────
const cardWrap = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--card-radius, 14px)',
  marginBottom: 16,
  overflow: 'hidden',
};
const header = {
  fontFamily: 'var(--font-display)',
  fontSize: 11, fontWeight: 800, letterSpacing: '0.2em',
  color: 'var(--text-2)',
  padding: '12px 14px 8px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const lockHint = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
  color: '#ffa726',
  padding: '2px 8px',
  background: 'rgba(255,167,38,0.12)',
  border: '1px solid rgba(255,167,38,0.3)',
  borderRadius: 999,
};
// Active (enabled) and disabled +/- buttons differ in opacity and
// touch feedback. Big targets (52×52) per Apple HIG / Material specs.
const btn = (enabled) => ({
  width: 52, height: 52,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: enabled ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.04)',
  border: enabled ? '1px solid rgba(0,212,255,0.3)' : '1px solid rgba(255,255,255,0.05)',
  borderRadius: 12,
  color: enabled ? 'var(--ice)' : 'var(--text-3)',
  cursor: enabled ? 'pointer' : 'not-allowed',
  WebkitTapHighlightColor: 'transparent',
  transition: 'transform 0.08s, background 0.15s',
  // Tap-down feedback handled via :active on the inline style — JSX
  // can't pseudo-classes, so we lean on browser default scale + use
  // touchAction:manipulation to suppress double-tap zoom on iOS.
  touchAction: 'manipulation',
});
const resetBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 7,
  color: 'var(--text-2)',
  padding: '5px 9px',
  fontSize: 11,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};
// May 2 2026 — Save & New button. Subtle ice tint so it reads as the
// "primary, constructive" action of the two coach buttons (Reset is
// destructive). Same shape/sizing as Reset for visual consistency.
const saveBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  background: 'rgba(0,212,255,0.10)',
  border: '1px solid rgba(0,212,255,0.35)',
  borderRadius: 7,
  color: 'var(--ice)',
  padding: '5px 9px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};
const hintBanner = {
  margin: '0 14px 12px',
  padding: '8px 10px',
  background: 'rgba(255,167,38,0.08)',
  border: '1px solid rgba(255,167,38,0.22)',
  borderRadius: 8,
  fontSize: 11,
  color: 'var(--text-2)',
  lineHeight: 1.45,
};
